import { useAuth } from "@/contexts/auth-context";
import { useCurrentUser } from "@/hooks/queries/useCurrentUser";
import { AccessReadinessCard } from "@/routes/profile/AccessReadinessCard";
import { ChangePasswordSection } from "@/components/account/ChangePasswordSection";
import { LegacyCertCard } from "@/routes/profile/LegacyCertCard";
import { ProfessionalDetailsCard } from "@/routes/profile/ProfessionalDetailsCard";
import { SubscriptionSection } from "@/routes/profile/SubscriptionSection";
import { CLINICIAN_ROLES } from "@/routes/route-access";

// Profile screen (Story 16.3 + 10.8 + 10.9 — UX Screen 14). Section order per the
// mockup: header (h1 = clinician name) → Professional details | Access & readiness
// (two-column grid) → Legacy AMPS certificate (self-hiding, legacy B2C only) →
// Subscription → Change password. Tokens only, bg-primary canvas (the AppShell
// provides it). 14b's gated shell chrome (pending pill, warn banner, locked nav)
// is AppShell/nav-config work (gh-488), not this screen's.

// The Screen-14 intro, verbatim. It stays exactly as it was for the readers it is
// true of — a clinician account really does have readiness information on this page.
export const CLINICIAN_INTRO =
  "Professional profile, access state, and readiness information for this clinician account.";

// Story 15.9 (#719, AC3) — the same sentence told an org_admin they hold a clinician
// account. One role-NEUTRAL replacement rather than one sentence per role: it is true
// for an org_admin, for a super_admin, and for the fifth role somebody adds later, and
// it names only what those readers can actually see here (Professional details, the
// change-email affordance, and Change password — the two things FR84 gives them). The
// exact wording is a declared undefined axis; what is pinned is the absence of a
// clinician claim.
export const NON_CLINICIAN_INTRO =
  "Your profile details and the account settings you manage yourself.";

export function ProfileScreen() {
  const { user, hasRole } = useAuth();
  const me = useCurrentUser();

  // h1 = full name from GET /auth/me (the JWT carries no names — PHI-free claims);
  // null/empty-string names fall back to the auth-context email, never a blank
  // heading (the ResumeEvaluationRoute pattern).
  const first = me.data?.first_name?.trim() || null;
  const last = me.data?.last_name?.trim() || null;
  const name = first && last ? `${first} ${last}` : (first ?? last);
  const heading = name ?? user?.email ?? "Profile";

  return (
    <section data-testid="profile-screen" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">{heading}</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          {hasRole(...CLINICIAN_ROLES) ? CLINICIAN_INTRO : NON_CLINICIAN_INTRO}
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <ProfessionalDetailsCard />
        <AccessReadinessCard />
      </div>

      {/* Self-hides unless the reader is a clinician AND a legacy one — role
          first, then origin (Decision 1 / AC9), the same predicate order the two
          cards above use. */}
      <LegacyCertCard />

      {/* Story 10.8 — billing display by origin (LearnWorlds / in-product / org-seat). */}
      <SubscriptionSection />

      <ChangePasswordSection />
    </section>
  );
}
